// @givmo/app-admin — auth state context + provider (+ admin auth ACTIONS).
//
// The context tracks the live Firebase session (`onAuthStateChanged`), exposes `isReady` for the
// cold-load gate + `isAuthenticated` for the route guard, AND exposes the admin auth ACTIONS the
// 07-1.1a screens drive: email sign-in/up, provider sign-in/up, password reset, and OTP send/confirm.
// The actions are THIN wrappers over @givmo/auth — they pass the app's `auth` instance + the
// `AdminAuthBackend` adapter (the only transport seam) into the package functions, which own ALL the
// orchestration (rememberMe persistence, popup-first OAuth, the Firebase-user rollback, OTP, the
// routing decision). The screens stay render-only and never touch Firebase/the backend directly.
//
// Parity: the Flutter webapp's `BeamGuard` only checks `FirebaseAuth.currentUser != null` and
// redirects unauthenticated paths to `/login-home`; `AuthBootstrap` subscribed to
// `authStateChanges()` at boot to cover cold-load with a persisted session. We mirror both: the
// provider subscribes once and the guard (src/router/*) reads the resulting state.

import { createContext, use, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type Auth,
  type User,
  type UserCredential,
} from 'firebase/auth';
import {
  adminSignInWithEmail,
  adminSignUpWithEmail,
  adminProviderSignIn,
  adminProviderSignUp,
  completeProviderRegistration,
  adminSendVerificationEmail,
  adminConfirmEmailCode,
  adminSendPasswordReset,
  adminSignInWithPasskey,
  adminSignInWithOrganizationSso,
  probePasskeySignIn,
  decideRoutingState,
  googleProvider,
  microsoftProvider,
  reauthenticateWithPassword,
  reauthenticateWithProvider,
  reconfirmWithPasskey,
  sessionSignInProvider,
  startTotpEnrollment,
  unenrollTotp,
  AuthError,
  type AdminAuthBackend,
  type PasskeyAssertionTransport,
  type PasskeyAvailability,
  type PasskeyReconfirmationTransport,
  type AdminProviderId,
  type AdminSignInResult,
  type AdminSignUpResult,
  type AuthRoutingState,
  type CompanyRegistration,
  type CompanyRegistrationResult,
  type OnboardingResolution,
  adoptSuiteSession,
  createSuiteSessionCancelHandle,
  establishSuiteSession,
  establishSuiteSessionAfterProvisioning,
  releaseSuiteSession,
  type SuiteSessionCancelHandle,
  type SuiteSessionEstablishOutcome,
  type SuiteSessionTransport,
} from '@givmo/auth';
import {
  createOrganizationSsoTransport,
  createPasskeyAssertionTransport,
  createPasskeyReconfirmationTransport,
  type AuthenticatorControls,
} from '@givmo/passkeys';
import { createAdminAuthBackend } from '../features/auth/admin-backend';
import { ensureBackendUser } from './ensure-backend-user';

export interface SignInWithEmailParams {
  email: string;
  password: string;
  rememberMe: boolean;
}

export interface SignUpWithEmailParams {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

/** The admin auth actions exposed to the screens (thin wrappers over @givmo/auth). */
export interface AuthActions {
  /** Email/password sign-in. Applies rememberMe persistence (in @givmo/auth) + resolves routing. */
  signInWithEmail: (params: SignInWithEmailParams) => Promise<AdminSignInResult>;
  /**
   * Email/password sign-up: create user → provision its Givmo row (with rollback on FAILURE). NO
   * company (NF-FIX21): the OTP screen proves the work email and then registers the company through
   * {@link AuthActions.completeProviderRegistration}.
   */
  signUpWithEmail: (params: SignUpWithEmailParams) => Promise<AdminSignUpResult>;
  /** Provider sign-in (existing-user expectation). New Firebase user → "not found, sign up". */
  signInWithProvider: (id: AdminProviderId) => Promise<AdminSignInResult>;
  /**
   * Passkey sign-in (AS-WEB2): a WebAuthn assertion exchanged for a Firebase custom token, then the
   * SAME routing decision every other sign-in resolves. Sign-in only — never a step-up, because
   * establishing a session is exactly what proving a live one must not do.
   */
  signInWithPasskey: () => Promise<AdminSignInResult>;
  /**
   * Organization sign-in (AS-WEB30): the handoff `/sso/callback` received, exchanged with the step's
   * PKCE verifier for a custom token, then the SAME routing decision every other sign-in resolves
   * (`adminSignInWithOrganizationSso`). Rejects with the exchange's own refusal (the door reads its
   * state from it), an `AuthError`, or the tail's `NoCompanyError`, which the portal lands.
   */
  signInWithOrganizationSso: (params: {
    handoffCode: string;
    codeVerifier: string;
  }) => Promise<AdminSignInResult>;
  /**
   * Whether the passkey affordance should render. Four states rather than a boolean because the
   * backend's per-IP options budget is small and shared with real sign-ins, so the CALLER caches each
   * answer for a different length of time — `features/auth/use-passkey-availability.ts` owns that
   * policy and explains why this probe is the whole gate. Never throws; silent while the backend
   * feature is dark, which is every environment today.
   */
  probePasskeyAvailability: () => Promise<PasskeyAvailability>;
  /** Provider sign-up (new-user expectation). Existing account → "already registered, sign in". */
  signUpWithProvider: (id: AdminProviderId) => Promise<UserCredential>;
  /**
   * Register the company for an account that already exists: a provider user, or (NF-FIX21) an email
   * account once its OTP proved the work email. Resolves to the registration result — a request-access
   * pivot (EP-SUITE2) surfaces here as a belt-and-suspenders backstop (both callers normally resolve the
   * branch via {@link resolveDomain} BEFORE calling this; `use-register-company.ts`).
   */
  completeProviderRegistration: (
    registration: CompanyRegistration,
  ) => Promise<CompanyRegistrationResult>;
  /**
   * Resolve the onboarding branch for the CURRENT signed-in caller with a verified work email
   * (`POST /admin/company/resolve`, EP-SUITE2). Only called when join-or-create is enabled; throws
   * (typically 403) when the email is not yet server-verified, and the caller falls through to create.
   */
  resolveDomain: () => Promise<OnboardingResolution>;
  /** Trigger the backend OTP email. */
  sendOtp: () => Promise<void>;
  /** Confirm the backend OTP code. Throws on an invalid code. */
  confirmOtp: (code: string) => Promise<void>;
  /** Send the password-reset email. */
  sendPasswordReset: (email: string) => Promise<void>;
  /**
   * Re-resolve the post-sign-in routing state for the CURRENT session (company + email-verified →
   * @givmo/auth's pure `decideRoutingState`). The verified/waiting-room screen polls this to detect
   * the company flipping to Active (→ `authenticated`) so it can auto-advance to the dashboard, and
   * the OTP screen re-resolves with `{ isProviderRegistration: true }` after a PROVIDER user
   * confirms their code (a companyless microsoft.com user then resolves `unregistered` → the
   * details screen, instead of the email flow's registered-and-pending assumption). Returns `null`
   * only when no company exists for a non-provider session (the package's null case).
   */
  refreshRoutingState: (opts?: {
    isProviderRegistration?: boolean;
  }) => Promise<AuthRoutingState | null>;
  /**
   * The suite-session establish for a Givmo `user` row provisioned OUTSIDE this context (AS-SUITE10)
   * — today the request-access screen's join-request filing, whose intake provisions the requester's
   * row inline when it is absent. Call it once, after that provisioning certainly happened (the 201),
   * with the account it provisioned. Fire-and-forget; never throws. Sends nothing when the attempt
   * retained for this sign-in already landed and exactly one login otherwise; only while that account
   * is still the one signed in; admitted through the sign-in's cancel handle, so a sign-out stops or
   * awaits it. The sign-up ceremonies' own points stay at one attempt per sign-in.
   */
  establishAfterProvisioning: (user: User) => void;
  /**
   * STEP-UP: re-confirm the CURRENT session with its own password (AS-WEB11, the portal's first
   * "Confirm it's you": its passkey settings). Not a sign-in — it cannot swap the session, and it
   * leaves `rememberMe` persistence alone. Rejects with `sessionExpired` when signed out, and with a
   * `SecondFactorRequiredError` an enrolled admin finishes in the sheet's code step.
   */
  reauthenticateWithPassword: (password: string) => Promise<UserCredential>;
  /** STEP-UP through the admin's OAuth provider. Popup-only — see `@givmo/auth`'s `reauth.ts`. */
  reauthenticateWithProvider: (id: AdminProviderId) => Promise<UserCredential>;
  /**
   * STEP-UP with one of the account's OWN passkeys (D-AS-17): `@givmo/auth`'s bound
   * `reconfirmWithPasskey`, then the suite session login re-run for the new session. Never the
   * unbound passkey sign-in.
   */
  confirmWithPasskey: () => Promise<void>;
  /**
   * AS-WEB14: authenticator-app enrolment for the LIVE session (`auth.currentUser`, never a render
   * snapshot: the "Confirm it's you" sheet re-issues the session before the retry), handed to the
   * Sign-in & security card. Optional, so a console's test doubles need not supply it.
   */
  authenticator?: AuthenticatorControls;
  /**
   * Mint a FRESH ID token for the live session (`auth.currentUser`), bypassing the cached one: the
   * step-up gate's retry must not carry the refused token. After a re-sign-in confirmation it also
   * re-runs the suite session login, once; a passkey confirmation already re-ran it.
   */
  refreshIdToken: () => Promise<string | null>;
}

export interface AuthState extends AuthActions {
  /** The current Firebase user, or `null` when signed out / not yet resolved. */
  user: User | null;
  /** `true` once the initial `onAuthStateChanged` emission has resolved (cold-load gate). */
  isReady: boolean;
  /** Whether a user is currently signed in. The route guard keys off this. */
  isAuthenticated: boolean;
  /** Sign out of the Firebase session. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export interface AuthProviderProps {
  auth: Auth;
  children: ReactNode;
  /**
   * Called whenever the auth state changes (after the React state update). The app wires this to
   * `router.invalidate()` so the guard's `beforeLoad` re-runs against the new session — mirroring the
   * TanStack Router auth-context recipe.
   */
  onAuthChange?: () => void;
  /**
   * The suite-session transport (AS-SUITE4). Present in the real app (`main.tsx` builds it from the
   * env base URL + the app's App Check provider); OMITTED in tests and the preview harness, where it
   * disables the whole rail so this provider behaves exactly as it did before AS-SUITE4.
   */
  suiteSession?: SuiteSessionTransport;
  /** Test seam: override the transport adapter. Production builds it from the generated SDK + auth. */
  backend?: AdminAuthBackend;
  /**
   * Test seam: override the passkey assertion transport. Production builds it from `givmoClient`
   * (the two `/authn` routes are not in the OpenAPI snapshot — see `@givmo/passkeys`'
   * `passkey-assertion-transport.ts`).
   */
  passkeyTransport?: PasskeyAssertionTransport;
  /** Test seam: override the passkey re-confirmation transport (`@givmo/passkeys` in production). */
  passkeyReconfirmation?: PasskeyReconfirmationTransport;
}

/** The admin set's providers, by the id a step-up names them with. */
function adminProvider(id: AdminProviderId) {
  return id === 'google' ? googleProvider() : microsoftProvider();
}

/**
 * The LIVE Firebase user for a step-up, or a typed refusal: `auth.currentUser`, never the `user`
 * render state, which an async failure handler may hold as a stale snapshot.
 */
function requireCurrentUser(auth: Auth): User {
  const current = auth.currentUser;
  if (current === null) throw new AuthError('sessionExpired');
  return current;
}

export function AuthProvider({
  auth,
  children,
  onAuthChange,
  backend,
  passkeyTransport,
  passkeyReconfirmation,
  suiteSession,
}: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [isReady, setIsReady] = useState(false);
  /**
   * The boot handoff, held as ONE in-flight promise rather than a "tried it" boolean.
   *
   * A second subscription (React StrictMode's deliberate double-mount) JOINS this attempt instead of
   * either firing a second request against a rate-limit budget shared with real sign-ins, or —
   * worse — skipping adoption and publishing a signed-out state while the first attempt is still in
   * flight, which would flash the door at a user who is signed in elsewhere. Same shape as the
   * charity portal's `ensureBackendUser`: one in-flight promise, joined by concurrent callers.
   */
  const bootHandoffRef = useRef<Promise<UserCredential | null> | null>(null);
  /**
   * True once the auth state has resolved ONCE, by any route — an adoption that answered, or a first
   * emission that already carried a user.
   *
   * ⚠ Both routes must set it, and the second one is the subtle half. Adoption is a BOOT behaviour;
   * a sign-out is not a boot. If this were set only on the adoption path, then a property that booted
   * with a persisted session — the ordinary returning-user case — would still be "unsettled" when the
   * user later signs out, and that sign-out's `null` emission would take the adopt branch and hand
   * the session straight back. With a healthy release that is merely a wasted request (the row is
   * already gone, so the handoff answers "no session"); with a FAILED release it re-adopts
   * immediately and the sign-out button looks broken.
   */
  const bootSettledRef = useRef(false);
  /** The uid a suite session was already established for — one establish per sign-in, not per emission. */
  const establishedUidRef = useRef<string | null>(null);
  /**
   * The establish retained for the current sign-in — the chokepoint's attempt, or the
   * post-provisioning one a sign-up ceremony retains here instead — so the post-provisioning helper
   * can hand it over as `attempted` and send nothing for a session that already landed.
   */
  const establishInFlightRef = useRef<Promise<SuiteSessionEstablishOutcome> | null>(null);
  /**
   * The current sign-in's cancel handle (AS-SUITE11) — every establish for the sign-in, the
   * chokepoint's and the post-provisioning one alike, is admitted through it, and `signOut` hands it
   * to the release.
   *
   * Establish is fire-and-forget, which is right for the sign-in it rides on and wrong for the
   * release that may follow it: releasing while the login POST is still in flight deletes nothing,
   * and the login then lands and sets the cookie anyway (AS-SUITE9). The release therefore cancels
   * this first and is sent only once any login it admitted has settled; a login NOT yet sent — still
   * behind its ID-token refresh or App Check mint, or a sign-up still provisioning — is simply never
   * sent, so the sign-out waits on nothing in front of the wire. Renewed only when a new sign-in
   * arrives after the old one ended — so a ceremony that outlives its sign-in may find ANOTHER
   * account's live handle here, which is why `establishAfterProvisioning` first checks that its
   * account is still the one signed in (AS-SUITE11 review F1); otherwise it finds it cancelled.
   */
  const establishCancelRef = useRef<SuiteSessionCancelHandle | null>(null);
  /**
   * The uid a post-provisioning establish already ran for (AS-SUITE7).
   *
   * A sign-up ceremony creates the Firebase user first and the Givmo `user` row afterwards, so an
   * establish on that first emission is doomed (the backend's deliberate no-user-row refusal) and
   * the once-per-uid guard above would never re-fire. Each ceremony therefore calls
   * `establishOnceAfterProvisioning` at the first point the row certainly exists — and a sign-up
   * reaches more than one such point (registration, then the OTP screen's row bootstrap), so this
   * keeps the ceremonies' post-provisioning attempt to exactly one per sign-in rather than one per
   * call site. The exposed `establishAfterProvisioning` action (AS-SUITE10) records here too but
   * does not consult it — see that action.
   */
  const provisionedEstablishUidRef = useRef<string | null>(null);
  /**
   * How many sign-up ceremonies are in flight (AS-SUITE9): the window between a ceremony starting
   * and the Firebase emission it produces being adjudicated by the chokepoint.
   *
   * The emission-time establish on a sign-up is doomed by construction — the row does not exist
   * yet — and firing it anyway cost one refused `POST /authn/session/login` per sign-up (one
   * `suite_session_refused credential` line on the server, one unit of a per-IP limiter shared
   * with real sign-ins) for nothing: the post-provisioning attempt is the one that establishes. So
   * while this is non-zero the chokepoint skips its attempt, and the ceremony's every exit — resolve,
   * throw, the request-access pivot — clears it in a `finally`. A COUNT rather than a boolean, so
   * two overlapping attempts (a double-click on a provider button) cannot have the first's exit
   * unmark the second's emission. A ref, not state: the emission is adjudicated inside the
   * listener, outside any render.
   *
   * In memory by design. A reload mid-ceremony (the page torn down between the user's creation and
   * its provisioning) loses the mark, and the emission the SDK replays at boot takes the ordinary
   * road — the chokepoint fires, is refused, and the post-provisioning attempt establishes: the
   * refused-then-established shape this mark removes from the common path, still correct, one
   * request dearer. Accepted residual.
   */
  const signUpCeremoniesInFlightRef = useRef(0);
  /**
   * The persistence choice to mirror onto the suite session. Only the email door has the checkbox;
   * every other door passes no persistence at all and so inherits the app's configured scope, which
   * is Firebase's `local` — so `true` is the honest mirror for them, and the default here.
   *
   * ⚠ Getting this wrong is a real defect, not a rounding error: `true` emits a cookie with
   * `Max-Age=1209600`, which survives a browser restart. Sending it for a user who UNCHECKED
   * "remember me" would silently sign the next person on a shared computer in as them.
   */
  const rememberMeRef = useRef(true);
  /**
   * A re-sign-in confirmation (password or provider) has run since the suite login was last re-run,
   * so the next forced refresh — the step-up gate's, after the prompt — re-runs it once. A passkey
   * confirmation re-runs it itself and clears this.
   */
  const suiteRefreshOwedRef = useRef(false);

  useEffect(() => {
    const publishSignedOut = () => {
      setUser(null);
      setIsReady(true);
      onAuthChange?.();
    };

    // Subscribe once. The stream emits the current user asynchronously on subscription, so this also
    // covers cold boot with a persisted Firebase session.
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      if (nextUser === null) {
        establishedUidRef.current = null;
        provisionedEstablishUidRef.current = null;
        // AS-SUITE4: a property that boots signed-out asks whether this browser holds a suite
        // session BEFORE `isReady` flips — someone signed in on another `*.givmo.io` property must
        // never see this door, not even for a frame. Every failure, the dark 404 included, answers
        // `null` and the door renders exactly as it does today.
        if (suiteSession !== undefined && !bootSettledRef.current) {
          const attempt = (bootHandoffRef.current ??= adoptSuiteSession({ auth, ...suiteSession }));
          void attempt.then((credential) => {
            bootSettledRef.current = true;
            // A credential re-enters THIS listener with a user, which publishes on the normal signed-in
            // path. Publishing a signed-out state here as well is what would flash the door.
            if (credential === null) publishSignedOut();
          });
          return;
        }
        publishSignedOut();
        return;
      }
      // AS-SUITE4: every completed sign-in arrives here — email, the MFA-resolved continuation, OAuth
      // by popup and by redirect return, a custom-token exchange — so this is the one place that
      // covers all of them. Fire-and-forget: the door already succeeded and a suite session is a
      // convenience on top of it. The primitive refuses BEFORE the wire on a restored session (its
      // `auth_time` is outside the backend's freshness bound) and on an adopted one (it carries the
      // handoff marker), which is what makes a call from here safe rather than chatty.
      if (suiteSession !== undefined && establishedUidRef.current !== nextUser.uid) {
        establishedUidRef.current = nextUser.uid;
        // AS-SUITE11: a sign-in after the last one ENDED gets a fresh cancel handle — marked or not,
        // because a sign-up's post-provisioning attempt is admitted through it too; otherwise it
        // shares the live one, so a sign-out stops every login this tab has in motion.
        if (establishCancelRef.current === null || establishCancelRef.current.cancelled) {
          establishCancelRef.current = createSuiteSessionCancelHandle();
        }
        if (signUpCeremoniesInFlightRef.current > 0) {
          // AS-SUITE9: a sign-up ceremony's emission — the row does not exist yet, so the attempt
          // would only be refused. Adjudicated as "no attempt": the uid is recorded above so a later
          // re-subscription fires no doomed one either, and the retained attempt is cleared so
          // `establishAfterProvisioning` sees NO attempt for this sign-in (never a leftover from an
          // earlier one) and sends exactly one request once the row exists.
          establishInFlightRef.current = null;
        } else {
          // Retained, not floated: see `establishInFlightRef`. Still fire-and-forget for this
          // listener — nothing here awaits it, and the sign-in publishes below regardless of how it
          // resolves.
          establishInFlightRef.current = establishSuiteSession({
            user: nextUser,
            rememberMe: rememberMeRef.current,
            cancelHandle: establishCancelRef.current,
            ...suiteSession,
          });
        }
      }
      // The auth state has resolved; a later `null` is a sign-out, not a boot. See `bootSettledRef`.
      bootSettledRef.current = true;
      setUser(nextUser);
      setIsReady(true);
      onAuthChange?.();
    });
    return unsubscribe;
  }, [auth, onAuthChange, suiteSession]);

  const value = useMemo<AuthState>(() => {
    // The transport adapter is the single seam; @givmo/auth owns all orchestration. Built per-`auth`.
    const adapter = backend ?? createAdminAuthBackend(auth);
    const passkeys = passkeyTransport ?? createPasskeyAssertionTransport();
    const organizationSso = createOrganizationSsoTransport();
    const reconfirmation = passkeyReconfirmation ?? createPasskeyReconfirmationTransport();
    /**
     * Re-run the suite session login for the live session after a confirmation, admitted through
     * this sign-in's cancel handle (a sign-out in progress sends nothing). Fire-and-forget, retained
     * like the chokepoint's; the primitive's own refusals still apply.
     */
    const rerunSuiteLogin = () => {
      suiteRefreshOwedRef.current = false;
      const current = auth.currentUser;
      if (suiteSession === undefined || current === null) return;
      const cancelHandle = establishCancelRef.current;
      if (cancelHandle === null || cancelHandle.cancelled) return;
      establishInFlightRef.current = establishSuiteSession({
        user: current,
        rememberMe: rememberMeRef.current,
        cancelHandle,
        ...suiteSession,
      });
    };
    // AS-SUITE7 ⚠ THE SIGN-UP CEREMONIES PROVISION AFTER THE EMISSION, SO THEY ESTABLISH AFTER IT —
    // AND, UNDER THE SIGN-UP MARK, ONLY THEN (AS-SUITE9).
    //
    // `adminSignUpWithEmail` creates the Firebase user, THEN provisions the Givmo `user` row (NF-FIX21:
    // no company until the OTP screen has proven the work email); `adminProviderSignUp` signs in a
    // brand-new provider user whose row only appears when `completeProviderRegistration` runs, or when
    // the OTP screen's `sendOtp` bootstraps it first. In every one of those the
    // emission reaches the chokepoint before the row exists, and an establish there is refused —
    // deliberately, uniform with a malformed token, so the route is never an account-existence
    // oracle. The primitive swallows the refusal, the once-per-uid guard holds, and a freshly
    // signed-up account had NO suite session until a later real sign-in. Under the sign-up mark
    // the chokepoint no longer spends that request at all.
    //
    // So each ceremony calls this at the first point the row certainly exists (through
    // `establishOnceAfterProvisioning`, below). The decision — wait for the retained attempt, send
    // nothing if it landed, otherwise exactly one more request — lives in `@givmo/auth`; this wrapper
    // only supplies the retained attempt (none, for an emission the mark covered; the refused one,
    // for an emission that arrived unmarked after a reload). Fire-and-forget: the ceremony's result
    // never waits on it; the attempt is retained in the same ref, and admitted through the sign-in's
    // cancel handle so a sign-out stops it or waits for it (AS-SUITE11).
    //
    // AS-SUITE10: exposed as an action, AS IS — the retained attempt's outcome decides, never an
    // earlier point having tried — for a row provisioned OUTSIDE this context: the request-access
    // screen files its join request through the SDK, and that intake provisions the requester's row
    // inline when absent, so its 201 is the first point the row CERTAINLY exists on every join path.
    // A Google requester reaches it with no attempt spent (the mark covered the emission, and the
    // pivot skips registration and the OTP bootstrap), so this is that sign-in's one login. An email
    // requester spent its one attempt at its sign-up's own row bootstrap (NF-FIX21: before the code,
    // with no company), which established — nothing is sent then. Charity's
    // `retryBackendUserBootstrap` hands the retained attempt over the same way (AS-SUITE11).
    const establishAfterProvisioning = (user: User): void => {
      // AS-SUITE11 review F1: a ceremony's provisioning can outlive the sign-in it was for, and by the
      // time it answers ANOTHER account may be signed in here — whose sign-in renewed the handle read
      // below. Establishing then would send this account's login through that sign-in's open handle,
      // after this account's own release: the next person at the machine adopted as it. Only the
      // account still signed in establishes, and a stale ceremony returns before it records any uid.
      if (auth.currentUser?.uid !== user.uid) return;
      if (suiteSession === undefined) return;
      // AS-SUITE11: a ceremony whose sign-in is being signed out — the release has cancelled the
      // handle and the local sign-out has not run yet, so the guard above still sees this account —
      // has nothing to establish, and returns here rather than claim a uid for a sign-in that is
      // ending.
      const cancelHandle = (establishCancelRef.current ??= createSuiteSessionCancelHandle());
      if (cancelHandle.cancelled) return;
      provisionedEstablishUidRef.current = user.uid;
      // The chokepoint's attempt for THIS sign-in — never a leftover from an earlier one. If the
      // emission has somehow not been delivered yet, claim the uid here so it fires no duplicate.
      const attempted =
        establishedUidRef.current === user.uid ? establishInFlightRef.current : null;
      establishedUidRef.current = user.uid;
      establishInFlightRef.current = establishSuiteSessionAfterProvisioning({
        user,
        rememberMe: rememberMeRef.current,
        attempted,
        cancelHandle,
        ...suiteSession,
      });
    };
    // AS-SUITE7: the ceremonies' points — ONE post-provisioning attempt per sign-in across them. A
    // sign-up passes several (the registration, then the OTP screen's row bootstrap, which fires on
    // mount and again on every resend), so once one has run for this uid the rest are no-ops,
    // whatever its outcome: a dark or refusing environment is charged one request per sign-up, not
    // one per resend, on a per-IP budget shared with real sign-ins.
    const establishOnceAfterProvisioning = (user: User): void => {
      if (provisionedEstablishUidRef.current === user.uid) return;
      establishAfterProvisioning(user);
    };
    // AS-SUITE9: run a ceremony that CREATES the Firebase user under the sign-up mark — set before
    // the user can exist, cleared on every exit (resolve, throw, the request-access pivot, which
    // resolves) — so no emission it produces reaches the chokepoint unmarked, and no sign-in that
    // follows it inherits the mark. Only the two wrappers that create the user take it:
    // `completeProviderRegistration` and `sendOtp` provision the row for a user that already exists.
    const underSignUpMark = async <T,>(ceremony: () => Promise<T>): Promise<T> => {
      signUpCeremoniesInFlightRef.current += 1;
      try {
        return await ceremony();
      } finally {
        signUpCeremoniesInFlightRef.current -= 1;
      }
    };
    return {
      user,
      isReady,
      isAuthenticated: user !== null,
      // AS-SUITE4: release the suite session BEFORE the local sign-out, on EVERY path — this is the
      // one `signOut` every screen in the app calls, which is why the release lives here rather than
      // at the call sites. Order is the point: afterwards the property may already have torn down
      // the state that got it here, and a property that signed out locally first could silently
      // re-establish the session the user just ended. It never rejects, so sign-out cannot fail
      // because a convenience layer did.
      // AS-SUITE9 + AS-SUITE11: and no login may land after the release, or the release deletes
      // nothing and the login then sets the cookie anyway — a sign-out that leaves a live suite
      // session behind, which on a shared machine is the next person signed in as this one. The
      // release takes this sign-in's cancel handle: a login not yet sent (its ID-token refresh or App
      // Check mint still pending, or a sign-up still provisioning ahead of its one attempt) never is,
      // and one already on the wire is waited for — cut by its own 8 s write timeout — before the
      // release goes. Never a timer on the wait: a timer releases while a login whose refresh
      // finishes late is still to be SENT. Worst case ≈ 16 s to the local `firebaseSignOut` (a
      // black-holed login sent just before the click, then a black-holed release; the release's own
      // mint is bounded and overlaps the wait) — was ≈ 46 s plus two unbounded mints.
      signOut: async () => {
        if (suiteSession !== undefined) {
          await releaseSuiteSession({ ...suiteSession, cancelHandle: establishCancelRef.current });
        }
        await firebaseSignOut(auth);
      },
      // ── Admin auth actions (thin @givmo/auth wrappers) ──────────────────────────────────────────
      // AS-SUITE4: record the checkbox so the suite session mirrors this door's persistence choice.
      // `false` yields a browser-SESSION suite cookie; `true` a 14-day one. This is the only door
      // that has the control, and the establish call at the chokepoint reads what it left here.
      signInWithEmail: (params) => {
        rememberMeRef.current = params.rememberMe;
        return adminSignInWithEmail(auth, adapter, params);
      },
      // AS-SUITE7: the row bootstrap has resolved by here (NF-FIX21: the sign-up provisions the row
      // and registers no company). A failure threw and rolled the Firebase user back, so nothing
      // reaches the establish.
      signUpWithEmail: (params) =>
        underSignUpMark(async () => {
          const result = await adminSignUpWithEmail(auth, adapter, params);
          establishOnceAfterProvisioning(result.credential.user);
          return result;
        }),
      signInWithProvider: (id) => adminProviderSignIn(auth, adapter, id),
      signInWithPasskey: () => adminSignInWithPasskey(auth, adapter, passkeys),
      probePasskeyAvailability: () => probePasskeySignIn(passkeys),
      // AS-WEB30: like the passkey's, no persistence choice (the chooser has no checkbox).
      signInWithOrganizationSso: (params) =>
        adminSignInWithOrganizationSso(auth, adapter, organizationSso, params),
      // AS-SUITE9 ⚠ THE ONE CEREMONY THAT DELIBERATELY THROWS ITS SESSION AWAY SENDS NOTHING.
      //
      // `adminProviderSignUp`'s existing-account arm signs in for real, discovers the account is not
      // new, and drops the Firebase session with `auth.signOut()` — the INSTANCE method, so the
      // context's `signOut` and its release never run — before failing with `emailAlreadyInUse`. An
      // EXISTING account has a backend row, so an establish fired on that emission would be ACCEPTED,
      // and someone told "already registered" would walk away leaving a live suite session behind for
      // the next boot of any property to adopt; on a shared machine, the next person signed in as
      // them. Under the sign-up mark no establish fires on that emission at all — the mark is set
      // before the popup opens and the emission is adjudicated inside it — so there is nothing to
      // release, and the wrapper is the bare ceremony. That ordering is the SDK's guarantee, not
      // luck: every sign-in shape ends in `AuthImpl._updateCurrentUser`, a serialized queue action
      // that fans the listener microtask out via `notifyAuthListeners` BEFORE the action returns, so
      // `signInWithPopup`/`signInWithCredential` cannot resolve — and this wrapper's `finally`
      // cannot clear the mark — before the emission was delivered; the arm's own `auth.signOut()`
      // null emission takes the same road (`@firebase/auth@1.13.3`, reproduced against the real SDK
      // in the cut review). (Until AS-SUITE9 this wrapper awaited the retained establish and
      // released, the R1 rule; its pin now asserts that nothing is sent.) A future ceremony that
      // drops a session for an account with a row OUTSIDE the mark needs the release-after-settle
      // ordering `signOut` has (the cancel handle, AS-SUITE11) — as would a future SDK that
      // delivered emissions after the ceremony's promise resolved.
      signUpWithProvider: (id) => underSignUpMark(() => adminProviderSignUp(auth, id)),
      // AS-SUITE7: the provider user's row appears with the registration (created or pivoted), so
      // this is the first certain point on the verified-provider path — unless the OTP bootstrap
      // below, or the email sign-up's own bootstrap, already took the one attempt, in which case this
      // is a no-op.
      completeProviderRegistration: async (registration) => {
        const result = await completeProviderRegistration(adapter, registration);
        const current = auth.currentUser;
        if (current !== null) establishOnceAfterProvisioning(current);
        return result;
      },
      resolveDomain: () => adapter.resolveDomain(),
      // The OTP endpoints 403 without a backend `users` row. The EMAIL sign-up always has one by the
      // time it OTPs (it provisions the row when it creates the account, NF-FIX21), but the
      // PROVIDER-first verification path (microsoft.com, 2026-08-18) and a returning account that
      // stopped at the code reach the send BEFORE any registration — so bootstrap the row here
      // (idempotent per uid, so the email sign-up's earlier bootstrap is not repeated; the charity
      // portal and donate p2p do the same dance).
      sendOtp: async () => {
        const current = auth.currentUser;
        if (current !== null) {
          await ensureBackendUser(current.uid, current.email);
          // AS-SUITE7: the row certainly exists once the bootstrap resolves — the first such point
          // on the provider-first (microsoft.com) path; a no-op after the email flow's registration.
          establishOnceAfterProvisioning(current);
        }
        await adminSendVerificationEmail(adapter);
      },
      // The confirm flips the email-verified flag on the Firebase RECORD, server-side. The SDK's cached
      // user and its cached ID token both predate that (EP-SUITE2-FIX2 (b)): `reload()` brings
      // `user.emailVerified` current — the request-access screen keys its verified gate on it — and
      // `getIdToken(true)` re-mints the token so every later request the Bearer interceptor sends
      // (it uses the CACHED token) carries `email_verified=true`, which the join-or-create routes
      // fail closed on. Best-effort: the code IS confirmed by then, and a refresh failure must not
      // read as "incorrect code" — the screens re-check the flag and the join POST re-mints anyway.
      confirmOtp: async (code) => {
        await adminConfirmEmailCode(adapter, code);
        await refreshVerifiedCredential(auth);
      },
      sendPasswordReset: (email) => adminSendPasswordReset(auth, email),
      refreshRoutingState: async (opts) => {
        const company = await adapter.getCompany();
        const emailVerified = company === null ? false : await adapter.isEmailVerified();
        return decideRoutingState({
          company,
          emailVerified,
          isProviderRegistration: opts?.isProviderRegistration,
        });
      },
      establishAfterProvisioning,
      // ── Step-up re-authentication (AS-WEB11: the passkey settings' "Confirm it's you") ─────────
      // The bound primitives, never the sign-in actions above (they can swap the session and
      // re-apply persistence) — see `@givmo/auth`'s `reauth.ts`.
      reauthenticateWithPassword: async (password) => {
        const current = requireCurrentUser(auth);
        suiteRefreshOwedRef.current = true;
        return reauthenticateWithPassword(auth, current, password);
      },
      reauthenticateWithProvider: async (id) => {
        const current = requireCurrentUser(auth);
        suiteRefreshOwedRef.current = true;
        return reauthenticateWithProvider(auth, current, adminProvider(id));
      },
      confirmWithPasskey: async () => {
        await reconfirmWithPasskey(auth, reconfirmation);
        rerunSuiteLogin();
      },
      authenticator: {
        uid: user?.uid ?? null,
        sessionSignInProvider: () => sessionSignInProvider(auth.currentUser),
        start: async () => startTotpEnrollment(requireCurrentUser(auth)),
        turnOff: async () => unenrollTotp(requireCurrentUser(auth)),
      },
      refreshIdToken: async () => {
        const token = (await auth.currentUser?.getIdToken(true)) ?? null;
        if (token !== null && suiteRefreshOwedRef.current) rerunSuiteLogin();
        return token;
      },
    };
  }, [auth, user, isReady, backend, passkeyTransport, passkeyReconfirmation, suiteSession]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

/**
 * Bring the signed-in user's cached record + ID token up to date after the backend OTP confirm set
 * the email-verified flag. Best-effort by design — see `confirmOtp` above for why a failure here is
 * swallowed rather than surfaced.
 */
async function refreshVerifiedCredential(auth: Auth): Promise<void> {
  const current = auth.currentUser;
  if (current === null) return;
  try {
    await current.reload();
    await current.getIdToken(true);
  } catch {
    // The confirm succeeded; the consumers of the flag / token re-check and re-mint on their own.
  }
}

/** Read the auth state. Throws if used outside an {@link AuthProvider}. */
export function useAuth(): AuthState {
  const ctx = use(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within an <AuthProvider>.');
  }
  return ctx;
}

export { AuthContext };
